import { forwardRef } from "react";
import { IMaskInput } from "react-imask";
import { cn } from "../lib/cn";
import { useFieldControlProps } from "./Field";

export interface PhoneInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "size" | "value" | "onChange"
  > {
  /** Unformatted GB mobile string in/out, e.g. "447700900123". */
  value?: string;
  /** Called with the unmasked value on every change. */
  onChange?: (value: string) => void;
  /** Country preset — only "GB" is supported initially. */
  defaultCountry?: "GB";
  /** Error state — error border + aria-invalid. */
  invalid?: boolean;
}

/** Class string copied verbatim from the Input atom (bare input, size md). */
const inputClasses =
  "w-full rounded-lg border border-border-control bg-surface-default px-3 transition-colors " +
  "outline-none focus-visible:border-border-focused focus-visible:ring-2 focus-visible:ring-border-focused-subtle " +
  "h-10 type-body-lg text-content-primary placeholder:text-content-disabled disabled:cursor-not-allowed disabled:opacity-38";

const invalidClasses =
  "border-surface-error focus-visible:border-surface-error";

/** GB mobile phone input — react-imask pattern, unmasked value in/out. */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      className,
      value,
      onChange,
      defaultCountry,
      invalid = false,
      id,
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": ariaInvalid,
      "aria-required": ariaRequired,
      ...props
    },
    ref,
  ) => {
    void defaultCountry; // GB-only preset — reserved for multi-country support
    const fieldControlProps = useFieldControlProps({
      id,
      describedBy: ariaDescribedBy,
      invalid: ariaInvalid || invalid,
      required: ariaRequired,
    });
    const resolvedInvalid = Boolean(fieldControlProps["aria-invalid"]);
    return (
      <IMaskInput
        inputRef={ref}
        data-slot="phone-input"
        {...fieldControlProps}
        mask="+00 0000 000000"
        unmask={true}
        value={value}
        onAccept={(accepted: string) => {
          onChange?.(accepted);
        }}
        placeholder="+44 7700 900123"
        className={cn(
          inputClasses,
          resolvedInvalid && invalidClasses,
          className,
        )}
        {...props}
      />
    );
  },
);
PhoneInput.displayName = "PhoneInput";
